import { Box } from '@abyss/web/ui/Box';
import { Brandmark } from '@abyss/web/ui/Brandmark';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getClientPortalConfig } from '../../frontends/GuestExperience/utils';
import { ClientNames, Constants, UHC_EXCHANGE_ICON } from '../Constants';
import { PortalIdentifier } from '../Portal';
import { PSXIcon } from '../PSXIcon/PSXIcon';

const handleLogoKeyDown = (e: React.KeyboardEvent, logoClick?: () => void) => {
  if ((e.key === 'Enter' || e.key === ' ') && logoClick) {
    e.preventDefault();
    try {
      logoClick();
    } catch {}
  }
};

interface GuestLogoProps {
  logoClick?: () => void;
  headerData?: {
    lob: string;
    coverage: string;
    signinUrl: string;
    brandmarkAffiliate: string;
    brandmarkVariant: string;
    languages: string;
  } | null;
}

export const GuestLogo: React.FC<GuestLogoProps> = ({
  headerData,
  logoClick: logoClick,
}) => {
  const hasCustomBrandmark =
    headerData?.brandmarkAffiliate && headerData?.brandmarkVariant;

  const uhcExchangeCustomBrandMark =
    headerData?.brandmarkAffiliate === UHC_EXCHANGE_ICON.AFFILIATE;
  const { t } = useTranslation();

  const clientName = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.CLIENT_NAME
  );
  const clientPortalConfig = getClientPortalConfig(clientName);
  const isEmpire = clientName?.toLowerCase() === ClientNames.EMPIRE;
  const isGeha = clientPortalConfig.portalIdentifier === PortalIdentifier.GEHA;

  const customIconName = isEmpire
    ? 'empire_logo'
    : isGeha
    ? 'geha_brandingV2'
    : hasCustomBrandmark && uhcExchangeCustomBrandMark
    ? UHC_EXCHANGE_ICON.AFFILIATE
    : null;

  return (
    <Box
      css={{
        'abyss-box-root': {
          display: 'flex',
          padding: 0,
          justifyContent: 'flex-start',
          alignItems: 'center',
          maxWidth: '242px',
          width: 'unset',
          height: '48px',
          backgroundColor: '$white',
        },
      }}
    >
      {customIconName ? (
        <PSXIcon
          aria-label={t('GUEST_LOGO_ACCESSIBILITY')}
          data-testid={
            isEmpire
              ? 'guest-empire-logo'
              : isGeha
              ? 'guest-geha-logo'
              : 'guest-exchange-custom-svg-icon'
          }
          icon={customIconName}
          onClick={logoClick}
          onKeyDown={(e) => handleLogoKeyDown(e, logoClick)}
          role="button"
          tabIndex={0}
        />
      ) : hasCustomBrandmark ? (
        <Brandmark
          affiliate={headerData.brandmarkAffiliate}
          aria-label={t('GUEST_LOGO_ACCESSIBILITY')}
          brand="uhc"
          color="blue"
          css={{
            'abyss-brandmark': {
              height: '48px',
              width: headerData?.brandmarkAffiliate.includes('community')
                ? '242px'
                : 'auto',
              display: 'block',
            },
          }}
          data-testid="guest-logo-brandmark"
          onClick={logoClick}
          onKeyDown={(e) => handleLogoKeyDown(e, logoClick)}
          role="button"
          size="100%"
          tabIndex={0}
          variant={headerData.brandmarkVariant}
        />
      ) : (
        <Brandmark
          affiliate="uhc"
          aria-label={t('GUEST_LOGO_ACCESSIBILITY')}
          brand="uhc"
          color="blue"
          css={{
            'abyss-brandmark': {
              height: '48px',
              width: 'auto',
              display: 'block',
            },
          }}
          data-testid="guest-logo-brandmark"
          onClick={logoClick}
          onKeyDown={(e) => handleLogoKeyDown(e, logoClick)}
          role="button"
          size="100%"
          tabIndex={0}
          variant="lockup"
        />
      )}
    </Box>
  );
};
